import React, { useReducer, useCallback } from 'react';
import {
	TFileId,
	TLanguageIDs,
	IItemForGetShortInfo,
	IDefaultLanguage,
	ICreateDishV2,
	IItemForGetShortDishInfo,
} from 'types';
import { createDishInitialState, CreateDishContext, TTypeOfModalWindow } from './createDishContext';
import { createDishReducer } from './createDishReducer';
import { CreateDishActionTypes } from './createDishActions';
import { v4 as uuidv4 } from 'uuid';

export const CreateDishState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createDishReducer, createDishInitialState);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: CreateDishActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const changeNameOfDish = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_NAME_OF_DISH,
			payload: {
				nameOfDish: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const changeExternalId = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: value,
			},
		});
	}, []);

	const generateUniqueExternalId = useCallback(() => {
		const uniqueExternalId = uuidv4();
		dispatch({
			type: CreateDishActionTypes.CHANGE_EXTERNAL_ID,
			payload: {
				externalId: uniqueExternalId,
			},
		});
	}, []);

	const setFetchedTranslations = useCallback(
		(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
			const translations = value.map((id) => ({ languageId: id, name: '' }));
			dispatch({
				type: CreateDishActionTypes.SET_FETCHED_TRANSLATIONS,
				payload: {
					translations,
					defaultCustomerLanguage,
				},
			});
		},
		[],
	);

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: CreateDishActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateDishActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const changeDescriptionTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							description: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateDishActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const toggleHasSchedulePriority = useCallback(() => {
		dispatch({
			type: CreateDishActionTypes.TOGGLE_HAS_SCHEDULE_PRIORITY,
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				fetchedCategories: value,
			},
		});
	}, []);

	const setSuggestedDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_SUGGESTED_DISH_CATEGORY,
			payload: {
				selectedSuggestedDishCategory: value,
			},
		});
	}, []);

	const setFetchedSuggestedDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_SUGGESTED_DISH_SUB_CATEGORIES,
			payload: {
				fetchedSuggestedDishSubCategories: value,
			},
		});
	}, []);

	const setSuggestedDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_SUGGESTED_DISH_SUB_CATEGORY,
			payload: {
				selectedSuggestedDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedSuggestedDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedSuggestedDishes.includes(el.id));
			const filteredFetchedSuggestedDishes = state.fetchedSuggestedDishes.filter((el) =>
				state.selectedSuggestedDishes.includes(el.id),
			);

			dispatch({
				type: CreateDishActionTypes.SET_FETCHED_SUGGESTED_DISHES,
				payload: {
					fetchedSuggestedDishes: [...filteredValue, ...filteredFetchedSuggestedDishes],
				},
			});
		},
		[state.selectedSuggestedDishes, state.fetchedSuggestedDishes],
	);

	const setSuggestedDishes = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_SUGGESTED_DISHES,
			payload: {
				selectedSuggestedDishes: value,
			},
		});
	}, []);

	const removeSuggestedDish = useCallback(
		(value: string) => {
			const updatedSelectedSuggestedDishes = state.selectedSuggestedDishes.filter((el) => el !== value);

			dispatch({
				type: CreateDishActionTypes.SET_SUGGESTED_DISHES,
				payload: {
					selectedSuggestedDishes: updatedSelectedSuggestedDishes,
				},
			});
		},
		[state.selectedSuggestedDishes],
	);

	const setAdditionalDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_ADDITIONAL_DISH_CATEGORY,
			payload: {
				selectedAdditionalDishCategory: value,
			},
		});
	}, []);

	const setFetchedAdditionalDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_ADDITIONAL_DISH_SUB_CATEGORIES,
			payload: {
				fetchedAdditionalDishSubCategories: value,
			},
		});
	}, []);

	const setAdditionalDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_ADDITIONAL_DISH_SUB_CATEGORY,
			payload: {
				selectedAdditionalDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedAdditionalDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedAdditionalDishes.includes(el.id));
			const filteredFetchedAdditionalDishes = state.fetchedAdditionalDishes.filter((el) =>
				state.selectedAdditionalDishes.includes(el.id),
			);

			dispatch({
				type: CreateDishActionTypes.SET_FETCHED_ADDITIONAL_DISHES,
				payload: {
					fetchedAdditionalDishes: [...filteredValue, ...filteredFetchedAdditionalDishes],
				},
			});
		},
		[state.selectedAdditionalDishes, state.fetchedAdditionalDishes],
	);

	const setAdditionalDishes = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_ADDITIONAL_DISHES,
			payload: {
				selectedAdditionalDishes: value,
			},
		});
	}, []);

	const removeAdditionalDish = useCallback(
		(value: string) => {
			const updatedSelectedAdditionalDishes = state.selectedAdditionalDishes.filter((el) => el !== value);

			dispatch({
				type: CreateDishActionTypes.SET_ADDITIONAL_DISHES,
				payload: {
					selectedAdditionalDishes: updatedSelectedAdditionalDishes,
				},
			});
		},
		[state.selectedAdditionalDishes],
	);

	const setModificationDishCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_MODIFICATION_DISH_CATEGORY,
			payload: {
				selectedModificationDishCategory: value,
			},
		});
	}, []);

	const setFetchedModificationDishSubCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_FETCHED_MODIFICATION_DISH_SUB_CATEGORIES,
			payload: {
				fetchedModificationDishSubCategories: value,
			},
		});
	}, []);

	const setModificationDishSubCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateDishActionTypes.SET_MODIFICATION_DISH_SUB_CATEGORY,
			payload: {
				selectedModificationDishSubCategory: value,
			},
		});
	}, []);

	const setFetchedModificationDishes = useCallback(
		(value: IItemForGetShortDishInfo[]) => {
			const filteredValue = value.filter((el) => !state.selectedModificationDishes.includes(el.id));
			const filteredFetchedModificationDishes = state.fetchedModificationDishes.filter((el) =>
				state.selectedModificationDishes.includes(el.id),
			);

			dispatch({
				type: CreateDishActionTypes.SET_FETCHED_MODIFICATION_DISHES,
				payload: {
					fetchedModificationDishes: [...filteredValue, ...filteredFetchedModificationDishes],
				},
			});
		},
		[state.selectedModificationDishes, state.fetchedModificationDishes],
	);

	const setModificationDishes = useCallback((value: string[]) => {
		dispatch({
			type: CreateDishActionTypes.SET_MODIFICATION_DISHES,
			payload: {
				selectedModificationDishes: value,
			},
		});
	}, []);

	const removeModificationDish = useCallback(
		(value: string) => {
			const updatedSelectedModificationDishes = state.selectedModificationDishes.filter((el) => el !== value);

			dispatch({
				type: CreateDishActionTypes.SET_MODIFICATION_DISHES,
				payload: {
					selectedModificationDishes: updatedSelectedModificationDishes,
				},
			});
		},
		[state.selectedModificationDishes],
	);

	const setDataForSubmit = useCallback((value: Nullable<ICreateDishV2>) => {
		dispatch({
			type: CreateDishActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CreateDishActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateDishActionTypes.RESET_FIELDS }), []);

	return (
		<CreateDishContext.Provider
			value={{
				setFileId,
				changeNameOfDish,
				changeDescription,
				setFetchedCustomers,
				setIDCustomer,
				changeExternalId,
				generateUniqueExternalId,
				setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				changeDescriptionTranslation,
				toggleHasSchedulePriority,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setFetchedRestaurants,
				setIDsRestaurant,
				setFetchedCategories,
				setSuggestedDishCategory,
				setFetchedSuggestedDishSubCategories,
				setSuggestedDishSubCategory,
				setFetchedSuggestedDishes,
				setSuggestedDishes,
				removeSuggestedDish,
				setAdditionalDishCategory,
				setFetchedAdditionalDishSubCategories,
				setAdditionalDishSubCategory,
				setFetchedAdditionalDishes,
				setAdditionalDishes,
				removeAdditionalDish,
				setModificationDishCategory,
				setFetchedModificationDishSubCategories,
				setModificationDishSubCategory,
				setFetchedModificationDishes,
				setModificationDishes,
				removeModificationDish,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				fileId: state.fileId,
				nameOfDish: state.nameOfDish,
				description: state.description,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				externalId: state.externalId,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				hasSchedulePriority: state.hasSchedulePriority,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				fetchedRestaurants: state.fetchedRestaurants,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				fetchedCategories: state.fetchedCategories,
				selectedSuggestedDishCategory: state.selectedSuggestedDishCategory,
				fetchedSuggestedDishSubCategories: state.fetchedSuggestedDishSubCategories,
				selectedSuggestedDishSubCategory: state.selectedSuggestedDishSubCategory,
				fetchedSuggestedDishes: state.fetchedSuggestedDishes,
				selectedSuggestedDishes: state.selectedSuggestedDishes,
				selectedAdditionalDishCategory: state.selectedAdditionalDishCategory,
				fetchedAdditionalDishSubCategories: state.fetchedAdditionalDishSubCategories,
				selectedAdditionalDishSubCategory: state.selectedAdditionalDishSubCategory,
				fetchedAdditionalDishes: state.fetchedAdditionalDishes,
				selectedAdditionalDishes: state.selectedAdditionalDishes,
				selectedModificationDishCategory: state.selectedModificationDishCategory,
				fetchedModificationDishSubCategories: state.fetchedModificationDishSubCategories,
				selectedModificationDishSubCategory: state.selectedModificationDishSubCategory,
				fetchedModificationDishes: state.fetchedModificationDishes,
				selectedModificationDishes: state.selectedModificationDishes,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CreateDishContext.Provider>
	);
};
